<script lang="ts" setup>
import { computed, type PropType } from 'vue';

import { orderformService } from '@exchange/libs/order/order-form/service/src';
import { OrderSide } from '@exchange/libs/order/shared-model/src/lib/order-essentials';

import orderFormEmitter from './order-form-event-bus';

const props = defineProps({
  onActiveSideChange: { type: Function as PropType<(type: OrderSide) => void>, required: false },
  activeSide: { type: String as PropType<OrderSide>, required: false },
  isFutures: { type: Boolean, required: false, default: false },
});

const storedSide = computed(() => props.activeSide ?? orderformService.data.side);
const isBuyOrder = computed(() => storedSide.value === OrderSide.BUY);

const onChange = (incomingSide: OrderSide) => {
  if (storedSide.value !== incomingSide) {
    orderformService.setSide(incomingSide);
    orderFormEmitter.emit('reset-form-softly');
  }

  if (props.onActiveSideChange) {
    props.onActiveSideChange(incomingSide);
  }
};
</script>

<template>
  <div class="order-form-side-switcher">
    <x-button
      class="order-form-side-switcher__button order-form-side-switcher__button--left"
      testid="order-side-buy"
      size="xs"
      rounded="sm"
      :class="{ 'order-form-side-switcher__button--underlined': isBuyOrder, 'order-form-side-switcher__button--base': !isBuyOrder }"
      :variant="isBuyOrder ? 'underlined-success' : 'base'"
      :text="isFutures ? `${$t('fundamentals.orders.side.buy')} / ${$t('fundamentals.orders.side.long')}` : $t('fundamentals.orders.side.buy')"
      @click="onChange(OrderSide.BUY)"
    />
    <x-button
      class="order-form-side-switcher__button order-form-side-switcher__button--right"
      testid="order-side-sell"
      size="xs"
      rounded="sm"
      :class="{ 'order-form-side-switcher__button--underlined': !isBuyOrder, 'order-form-side-switcher__button--base': isBuyOrder }"
      :variant="!isBuyOrder ? 'underlined-error' : 'base'"
      :text="isFutures ? `${$t('fundamentals.orders.side.sell')} / ${$t('fundamentals.orders.side.short')}` : $t('fundamentals.orders.side.sell')"
      @click="onChange(OrderSide.SELL)"
    />
  </div>
</template>

<style lang="scss">
.order-form-side-switcher {
  display: flex;
  flex-direction: row;
  border: 1px solid rgb(var(--v-theme-elevation-2));
  border-radius: var(--app-border-radius);

  &__button {
    flex: 1 1 50%;
    font-weight: var(--font-weight-medium);
    text-transform: uppercase;
    transition: none;

    &--left {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
    }

    &--right {
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
    }

    &--base {
      background-color: transparent;
    }

    & + & {
      margin: 0;
    }
  }
}

.order-form-side-switcher__button:not(.order-form-side-switcher__button--underlined) {
  &:hover {
    background-color: rgb(var(--v-theme-elevation-3)) !important;
  }
}
</style>
